import { ViewStyle } from 'react-native';

/**
 * Configuration options for grid appearance
 */
export interface GridConfig {
  /** Fixed cell size in pixels (default: 50) */
  cellSize?: number;
  /** Dots vs lines rendering mode (default: false) */
  dotMode?: boolean;
  /** Dot radius in pixels when in dot mode (default: 2) */
  dotRadius?: number;
  /** Line width in pixels when in line mode (default: 1) */
  lineWidth?: number;
  /** Grid color (default: 'grey') */
  color?: string;
  /** Grid opacity (default: 0.2) */
  opacity?: number;
}

/**
 * Props for the InfiniteGrid component
 */
export interface InfiniteGridProps extends GridConfig {
  /** Current zoom level from parent zoomable view */
  zoomLevel: number;
  /** Horizontal offset from parent zoomable view */
  offsetX: number;
  /** Vertical offset from parent zoomable view */
  offsetY: number;
  /** Viewport width for culling calculations */
  viewportWidth: number;
  /** Viewport height for culling calculations */
  viewportHeight: number;
}

/**
 * Props for the ZoomableGridView wrapper component
 */
export interface ZoomableGridViewProps extends GridConfig {
  /** Child components to render inside the zoomable view */
  children: React.ReactNode;
  /** Initial zoom level (default: 1) */
  initialZoom?: number;
  /** Minimum zoom level (default: 0.2) */
  minZoom?: number;
  /** Maximum zoom level (default: 5) */
  maxZoom?: number;
  /** Zoom step increment (default: 0.5) */
  zoomStep?: number;
  /** Content area width (default: 10000) */
  contentWidth?: number;
  /** Content area height (default: 10000) */
  contentHeight?: number;
  /** Callback when zoom level changes */
  onZoomChange?: (zoomLevel: number, offsetX: number, offsetY: number) => void;
  /** Additional styles for the container */
  style?: ViewStyle;
  /** Whether to bind content to borders (default: true) */
  bindToBorders?: boolean;
  /** Whether to show the grid (default: true) */
  showGrid?: boolean;
  /** Platform-specific initial offset X */
  initialOffsetX?: number;
  /** Platform-specific initial offset Y */
  initialOffsetY?: number;
  /** 
   * Ref to existing ReactNativeZoomableView to read values from.
   * If provided, component acts as grid-only (no wrapper).
   * If not provided, component creates its own zoomable wrapper.
   */
  zoomableViewRef?: React.RefObject<any>;
  /**
   * If using external zoomableViewRef, you should provide the current zoom state.
   * The parent's onTransform handler should update these values.
   */
  externalZoomLevel?: number;
  externalOffsetX?: number;
  externalOffsetY?: number;
}

/**
 * Transform event data from ReactNativeZoomableView
 */
export interface ZoomTransformEvent {
  zoomLevel: number;
  offsetX: number;
  offsetY: number;
}

/**
 * Default grid configuration values
 */
export const DEFAULT_GRID_CONFIG: Required<GridConfig> = {
  cellSize: 50,
  dotMode: false,
  dotRadius: 2,
  lineWidth: 1,
  color: 'grey',
  opacity: 0.2,
};

/**
 * Default zoomable view configuration
 */
export const DEFAULT_ZOOM_CONFIG = {
  initialZoom: 1,
  minZoom: 0.2,
  maxZoom: 5,
  zoomStep: 0.5,
  contentWidth: 10000,
  contentHeight: 10000,
  bindToBorders: true,
  showGrid: true,
};

